import Keycloak from "keycloak-js";
import { getIdpHint } from "../util/idp";

const _kc = new Keycloak("/keycloak.json");

/**
 * Initializes Keycloak instance and calls the provided callback function if successfully authenticated.
 *
 * @param onAuthenticatedCallback
 */
const initKeycloak = (onAuthenticatedCallback) => {
  _kc
    .init({
      onLoad: "check-sso",
      silentCheckSsoRedirectUri:
        window.location.origin + "/silent-check-sso.html",
      checkLoginIframe: false,
      pkceMethod: "S256",
    })
    .then((authenticated) => {
      onAuthenticatedCallback();
    })
    .catch(console.error);
};

const clearOrgContext = () => {
  sessionStorage.removeItem("chosenEmployerId");
  sessionStorage.removeItem("hasMultipleEmployers");
};

/**
 * Handles Keycloak login flow.
 * - If an idpHint is provided, uses it for SSO login.
 * - If no idpHint is provided and only one IdP is configured for the subdomain, uses that IdP's hint.
 * - If multiple IdPs are configured and no hint is provided, falls back to generic Keycloak login (should not occur in current UI).
 * Always clears organization context before login.
 */
const doLogin = (idpHint) => {
  clearOrgContext(); // Ensure org context is cleared before login
  if (idpHint) {
    return _kc.login({ idpHint });
  }
  const idpConfig = getIdpHint();
  // If single IdP, use its hint
  if (idpConfig && idpConfig.idpHint) {
    return _kc.login({ idpHint: idpConfig.idpHint });
  }
  // If multiple IdPs, fallback to default if login with (no hint)
  // given the current UI this should not happen
  return _kc.login();
};

const doLogout = () => {
  clearOrgContext(); // Ensure org context is cleared before logout
  return _kc.logout();
};

const getToken = () => _kc.token;

const isLoggedIn = () => !!_kc.token;

const updateToken = (successCallback) =>
  _kc.updateToken(5).then(successCallback).catch(doLogin);

const getUsername = () => _kc.tokenParsed?.preferred_username;

const hasRole = (roles) => {
  // Make sure roles is an array and not undefined
  if (!roles || !Array.isArray(roles) || roles.length === 0) {
    return false;
  }
  return roles.some((role) => _kc.hasRealmRole(role));
};

const getTokenClaim = (claimName) => {
  return _kc.tokenParsed ? _kc.tokenParsed[claimName] : null;
};

const UserService = {
  initKeycloak,
  doLogin,
  doLogout,
  isLoggedIn,
  getToken,
  updateToken,
  getUsername,
  hasRole,
  getTokenClaim,
};

export default UserService;
